import React, { useState, useRef } from 'react';
import emailjs from '@emailjs/browser';
import { EMAILJS_CONFIG } from '../config/emailjs.config';
import SEO from '../components/SEO';
import './Contact.css';

const Contact = () => {
    const form = useRef();

    const structuredData = {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        name: 'Contact Avenis Global',
        description:
            'Get in touch with Avenis Global for e-commerce partnerships, wholesale solutions, and digital marketing services.',
        url: 'https://avenisglobal.com/contact',
        mainEntity: {
            '@type': 'Organization',
            name: 'Avenis Global',
            contactPoint: {
                '@type': 'ContactPoint',
                contactType: 'customer service',
                url: 'https://avenisglobal.com/contact',
                availableLanguage: 'English',
            },
        },
    };

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        company: '',
        subject: '',
        message: '',
        consent: false,
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', null

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value,
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus(null);

        // EmailJS configuration
        const serviceId = EMAILJS_CONFIG.SERVICE_ID;
        const templateId = EMAILJS_CONFIG.TEMPLATE_ID;
        const publicKey = EMAILJS_CONFIG.PUBLIC_KEY;

        // Prepare template parameters
        const templateParams = {
            name: formData.name,
            email: formData.email,
            title: formData.company || 'Not provided',
            subject: formData.subject,
            message: formData.message,
            to_email: EMAILJS_CONFIG.TO_EMAIL,
        };

        // Send email using EmailJS
        emailjs.send(serviceId, templateId, templateParams, publicKey).then(
            (response) => {
                console.log('SUCCESS!', response.status, response.text);
                setSubmitStatus('success');
                setIsSubmitting(false);

                // Reset form
                setFormData({
                    name: '',
                    email: '',
                    company: '',
                    subject: '',
                    message: '',
                    consent: false,
                });
            },
            (error) => {
                console.log('FAILED...', error);
                setSubmitStatus('error');
                setIsSubmitting(false);
            }
        );
    };

    return (
        <div className="contact-page">
            <SEO
                title="Contact"
                description="Get in touch with Avenis Global for e-commerce partnerships, wholesale solutions, and digital marketing services. Contact us today for a consultation."
                keywords="contact Avenis Global, e-commerce consultation, wholesale partnership inquiry, digital marketing contact, Amazon wholesale contact"
                url="https://avenisglobal.com/contact"
                structuredData={structuredData}
            />
            <div className="contact-hero">
                <div className="container">
                    <h1>Contact</h1>
                    <p>
                        Get in touch with us for your projects. Let's find the
                        best solution together.
                    </p>
                </div>
            </div>

            <div className="container">
                <div className="contact-content">
                    <div className="contact-info">
                        <h2>
                            <span className="dashboard-title-text">Get in</span>
                            <span className="dashboard-title-highlight">
                                {' '}
                                Touch with Us
                            </span>
                        </h2>
                        <p>
                            If you want to get professional support for your
                            e-commerce projects, you can contact us by filling
                            out the form or using our contact information
                            directly.
                        </p>

                        <div className="contact-methods">
                            <div className="contact-method">
                                <div className="method-icon method-icon-email">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                                        <polyline points="22,6 12,13 2,6"></polyline>
                                    </svg>
                                </div>
                                <div className="method-info">
                                    <h4>Email</h4>
                                    <p>
                                        <a
                                            href="mailto:sales@avenisglobal.com"
                                            className="page-contact-link"
                                        >
                                            sales@avenisglobal.com
                                        </a>
                                    </p>
                                    <span>We respond 24/7</span>
                                </div>
                            </div>

                            <div className="contact-method">
                                <div className="method-icon method-icon-phone">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                                    </svg>
                                </div>
                                <div className="method-info">
                                    <h4>Phone</h4>
                                    <p>
                                        <a
                                            href="tel:+17276775939"
                                            className="page-contact-link"
                                        >
                                            +1 (727) 677-5939
                                        </a>
                                    </p>
                                    <div className="contact-hours">
                                        <div className="hours-item">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="16"
                                                height="16"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                className="hours-icon"
                                            >
                                                <path d="M12 6v6l4 2"></path>
                                                <circle
                                                    cx="12"
                                                    cy="12"
                                                    r="10"
                                                ></circle>
                                            </svg>
                                            <span>Mon-Fri: 9AM-6PM EST</span>
                                        </div>
                                        <div className="hours-item">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="16"
                                                height="16"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                className="hours-icon"
                                            >
                                                <path d="M12 6v6l4 2"></path>
                                                <circle
                                                    cx="12"
                                                    cy="12"
                                                    r="10"
                                                ></circle>
                                            </svg>
                                            <span>Sat: 10AM-2PM EST</span>
                                        </div>
                                        <div className="hours-item">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="16"
                                                height="16"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                className="hours-icon hours-icon-closed"
                                            >
                                                <path d="M12 6v6l4 2"></path>
                                                <circle
                                                    cx="12"
                                                    cy="12"
                                                    r="10"
                                                ></circle>
                                            </svg>
                                            <span>Sun: Closed</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="contact-method">
                                <div className="method-icon method-icon-address">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                                        <circle cx="12" cy="10" r="3"></circle>
                                    </svg>
                                </div>
                                <div className="method-info">
                                    <h4>Address</h4>
                                    <p>
                                        7901 4th St N Ste 300
                                        <br />
                                        St. Petersburg, Florida, 33702
                                    </p>
                                    <span>
                                        Make an appointment for office visit
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="contact-form-container">
                        <div className="form-header">
                            <h2>Send us a Message</h2>
                            <p>
                                Fill out the form below and we'll get back to
                                you within 24-48 hours.
                            </p>
                        </div>
                        <form
                            ref={form}
                            className="contact-form"
                            onSubmit={handleSubmit}
                        >
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="name">Full Name *</label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        placeholder="Your full name"
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="email">
                                        Email Address *
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="your@email.com"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="company">
                                    Company (Optional)
                                </label>
                                <input
                                    type="text"
                                    id="company"
                                    name="company"
                                    value={formData.company}
                                    onChange={handleChange}
                                    placeholder="Your company name"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="subject">Subject *</label>
                                <input
                                    type="text"
                                    id="subject"
                                    name="subject"
                                    value={formData.subject}
                                    onChange={handleChange}
                                    placeholder="What's this about?"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="message">Message *</label>
                                <textarea
                                    id="message"
                                    name="message"
                                    rows="6"
                                    value={formData.message}
                                    onChange={handleChange}
                                    placeholder="Tell us more about your inquiry..."
                                    required
                                ></textarea>
                            </div>

                            <div className="form-group consent-group">
                                <input
                                    type="checkbox"
                                    id="consent"
                                    name="consent"
                                    checked={formData.consent}
                                    onChange={handleChange}
                                    required
                                />
                                <label htmlFor="consent">
                                    I agree to Avenis Global LLC processing my
                                    personal data in accordance with the{' '}
                                    <a
                                        href="/privacy-policy"
                                        target="_blank"
                                        className="privacy-link"
                                    >
                                        Privacy Policy
                                    </a>
                                    .
                                </label>
                            </div>

                            {/* Status Messages */}
                            {submitStatus === 'success' && (
                                <div className="form-status success">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="20"
                                        height="20"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M9 12l2 2 4-4"></path>
                                        <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"></path>
                                    </svg>
                                    <span>
                                        Message sent successfully! We'll get
                                        back to you soon.
                                    </span>
                                </div>
                            )}

                            {submitStatus === 'error' && (
                                <div className="form-status error">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="20"
                                        height="20"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <line
                                            x1="15"
                                            y1="9"
                                            x2="9"
                                            y2="15"
                                        ></line>
                                        <line
                                            x1="9"
                                            y1="9"
                                            x2="15"
                                            y2="15"
                                        ></line>
                                    </svg>
                                    <span>
                                        Failed to send message. Please try again
                                        or contact us directly.
                                    </span>
                                </div>
                            )}

                            <button
                                type="submit"
                                className={`submit-btn ${
                                    isSubmitting ? 'submitting' : ''
                                }`}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="20"
                                            height="20"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="spinner"
                                        >
                                            <path d="M21 12a9 9 0 11-6.219-8.56"></path>
                                        </svg>
                                        Sending...
                                    </>
                                ) : (
                                    <>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="20"
                                            height="20"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z"></path>
                                            <path d="m21.854 2.147-10.94 10.939"></path>
                                        </svg>
                                        Send Message
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="20"
                                            height="20"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="arrow-icon"
                                        >
                                            <path d="M7 7h10v10"></path>
                                            <path d="M7 17 17 7"></path>
                                        </svg>
                                    </>
                                )}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Contact;
